import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Smartphone, BellRing, Check, Lock, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { whenFree } from "@/lib/popupQueue";
import { isIOS, isAndroid, isStandalone, canInstall, promptInstall, enablePush } from "@/lib/push";
import { InstallSteps } from "@/components/InstallSteps";

const SEEN = "itz_points_gate_seen";
const isMobile = () => isIOS() || isAndroid();

function Step({ n, icon: Icon, title, text, done, children, testId }) {
  return (
    <div className={`rounded-2xl border p-4 text-left ${done ? "bg-emerald-50 border-emerald-200" : "bg-white border-pink-100"}`} data-testid={testId}>
      <div className="flex items-center gap-3">
        <span className={`w-10 h-10 shrink-0 rounded-xl flex items-center justify-center ${done ? "bg-emerald-500 text-white" : "bg-rose-50 text-rose-500"}`}>{done ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}</span>
        <span className="flex-1"><span className="block font-bold text-slate-800 text-sm">Schritt {n}: {title}</span><span className="block text-xs text-slate-500 mt-0.5">{done ? "Erledigt ✓" : text}</span></span>
      </div>
      {!done && children}
    </div>
  );
}

function GateSheet({ g, onClose, recheck }) {
  const [busy, setBusy] = useState(false);
  const [guide, setGuide] = useState(false);
  const needApp = g.mobile && !g.app && !isStandalone();
  const push = async () => {
    setBusy(true);
    try { await enablePush(); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const install = async () => { if (canInstall() && !isIOS()) { if (await promptInstall()) toast.success("App wird installiert – öffne sie jetzt vom Home-Bildschirm"); } else setGuide(true); };
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label="Punkte freischalten" className="fixed inset-0 z-[93] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" data-testid="points-gate-popup">
      <div className="relative w-full sm:max-w-md max-h-[94dvh] overflow-y-auto bg-[#fff7f9] rounded-t-[32px] sm:rounded-[32px] shadow-2xl fade-rise p-6 text-center">
        <button type="button" onClick={onClose} aria-label="Schließen" data-testid="points-gate-close" className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white flex items-center justify-center"><X className="w-4 h-4" /></button>
        <span className="mx-auto w-16 h-16 rounded-2xl bg-rose-500 text-white flex items-center justify-center shadow-lg shadow-rose-300/60"><Lock className="w-7 h-7" /></span>
        <h2 className="mt-4 font-display text-2xl font-black text-slate-900" data-testid="points-gate-title">Punkte freischalten</h2>
        <p className="mt-1 text-sm text-slate-500">Punkte sammeln kannst du nur mit {g.mobile ? "installierter App und " : ""}aktiven Push-Benachrichtigungen. {g.bonus_claimed ? "" : "Als Dankeschön gibt's einmalig +50 Punkte!"}</p>
        <div className="mt-5 space-y-3">
          {g.mobile && (
            <Step n={1} icon={Smartphone} title="App installieren" text="Hol dir itzPlay auf deinen Home-Bildschirm." done={!needApp} testId="points-gate-step-app">
              {guide ? <div className="mt-3"><InstallSteps compact /></div> : <button type="button" onClick={install} data-testid="points-gate-install-btn" className="mt-3 w-full rounded-full bg-rose-500 text-white font-bold py-2.5 text-sm">App installieren</button>}
            </Step>
          )}
          <Step n={g.mobile ? 2 : 1} icon={BellRing} title="Push-Benachrichtigungen" text={needApp && isIOS() ? "Auf dem iPhone erst nach der Installation in der App möglich." : "Erlaube Benachrichtigungen, damit du nichts verpasst."} done={g.push} testId="points-gate-step-push">
              {!(needApp && isIOS()) && <button type="button" onClick={push} disabled={busy} data-testid="points-gate-push-btn" className="mt-3 w-full rounded-full bg-rose-500 text-white font-bold py-2.5 text-sm inline-flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Push aktivieren</button>}
          </Step>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function PointsGate() {
  const { user, refreshUser } = useAuth();
  const [g, setG] = useState(null);
  const [open, setOpen] = useState(false);
  const recheck = useCallback(async () => {
    const { data } = await api.post("/points/gate", { mobile: isMobile() });
    setG(data);
    if (data.unlocked) setOpen(false);
    if (data.awarded) { toast.success(`+${data.awarded} Punkte – Punktesammeln freigeschaltet 🎉`); setTimeout(refreshUser, 800); }
    return data;
  }, [refreshUser]);
  useEffect(() => {
    if (!user) return;
    recheck().then((d) => { if (!d.unlocked && !sessionStorage.getItem(SEEN)) whenFree("points-gate", () => { sessionStorage.setItem(SEEN, "1"); setOpen(true); }); }).catch(() => {});
    const vis = () => document.visibilityState === "visible" && recheck().catch(() => {});
    document.addEventListener("visibilitychange", vis);
    window.addEventListener("push-changed", vis);
    return () => { document.removeEventListener("visibilitychange", vis); window.removeEventListener("push-changed", vis); };
  }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!user || !g || g.unlocked) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="points-gate-banner" className="mb-4 w-full flex items-center gap-3 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 text-left">
        <Lock className="w-5 h-5 text-amber-600 shrink-0" />
        <span className="flex-1 text-sm"><span className="font-bold text-amber-800">Punktesammeln pausiert.</span> <span className="text-amber-700">{g.mobile ? "App installieren & Push aktivieren" : "Push aktivieren"}, um Punkte zu bekommen.</span></span>
        <span className="text-xs font-bold text-rose-600 shrink-0">Jetzt freischalten</span>
      </button>
      {open && <GateSheet g={g} onClose={() => setOpen(false)} recheck={recheck} />}
    </>
  );
}
